import Service, { inject as service } from '@ember/service';

import { RateLimitError } from 'later/errors/fetch';
import { UploadCredentialsError } from 'later/errors/upload-credentials';
import { fetch } from 'later/utils/fetch';
import { STRATEGY } from 'shared/utils/retry';

import type { AwsCredentialIdentity, AwsCredentialIdentityProvider } from '@smithy/types';
import type OfflineService from 'later/services/offline';

export interface UploadCredentials {
  bucket: string;
  keyPrefix: string;
  region: string;
  useAccelerateEndpoint: boolean;
  identity: AwsCredentialIdentity & { expiration: Date };
}

/** S3 client options for one upload; the provider refreshes the issued set itself */
export interface UploadClientOptions {
  region: string;
  useAccelerateEndpoint: boolean;
  credentials: AwsCredentialIdentityProvider;
}

/** The resource an upload is for; the backend authorizes the caller against it and checks its account's flag */
export type UploadTargetParams =
  | { kind: 'media_item'; group_id: string }
  | { kind: 'linkinbio_attachment'; group_id: string }
  | { kind: 'media_kit_image'; social_identity_id: string };

/** Where an upload writes; `clientOptions` is absent for the Cognito default */
export interface UploadTarget {
  bucket: string;
  keyPrefix: string;
  clientOptions?: UploadClientOptions;
}

export interface UploadCredentialsResponse {
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  expiration: string;
  bucket: string;
  key_prefix: string;
  region: string;
  use_accelerate_endpoint: boolean;
}

// @smithy/core treats an identity within 5 minutes of expiry as expired and asks the provider on every
// request, so the provider refreshes at the same threshold.
export const CREDENTIALS_REFRESH_WINDOW_MS = 5 * 60 * 1000;

// The backend throttles issuance in fixed 60-second windows and answers a 429 with the seconds left
// in the window; a 429 without the header is held for a whole window, and no header can hold longer.
export const RATE_LIMIT_HOLD_MS = 60 * 1000;

// A 200 whose body is not the credentials contract (HTML from a proxy, a deploy gap) fails like an outage.
const isCredentialsResponse = (data: unknown): data is UploadCredentialsResponse => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const response = data as Partial<UploadCredentialsResponse>;
  const stringFields = [
    response.access_key_id,
    response.secret_access_key,
    response.session_token,
    response.expiration,
    response.bucket,
    response.key_prefix,
    response.region
  ];
  return (
    stringFields.every((field) => typeof field === 'string') &&
    typeof response.use_accelerate_endpoint === 'boolean' &&
    !Number.isNaN(Date.parse(response.expiration ?? ''))
  );
};

// The SDK and the provider compare expiry with the client clock, so express it in client time
// using the response's Date header; a client clock behind the server would otherwise refresh late.
const toClientTime = (expiration: string, serverDate: string | null): Date => {
  const serverNow = Date.parse(serverDate ?? '');
  return Number.isNaN(serverNow) ? new Date(expiration) : new Date(Date.now() + Date.parse(expiration) - serverNow);
};

/**
 * Short-lived, prefix-scoped S3 credentials for uploads, issued by the backend.
 * No cache: each upload fetches its own set and refreshes it itself, so a queue costs one request
 * per file against the backend's per-user issuance limit (120 a minute by default). After a 429 the
 * rest of the queue fails without a request until the throttle window has passed, since every
 * attempt in the window counts against the limit. The hold covers the whole tab: the backend also
 * throttles per account and does not say which scope tripped, so a contributor spanning accounts
 * can be held for a group the backend would still have served.
 */
export default class UploadCredentialsService extends Service {
  @service declare offline: OfflineService;

  #rateLimitedUntil = 0;

  async fetchTarget(target: UploadTargetParams): Promise<Required<UploadTarget>> {
    let issued = await this.fetchCredentials(target);
    const { bucket, keyPrefix, region, useAccelerateEndpoint } = issued;

    return {
      bucket,
      keyPrefix,
      clientOptions: {
        region,
        useAccelerateEndpoint,
        credentials: async () => {
          if (issued.identity.expiration.getTime() - Date.now() <= CREDENTIALS_REFRESH_WINDOW_MS) {
            issued = await this.fetchCredentials(target);
          }
          return issued.identity;
        }
      }
    };
  }

  async fetchCredentials(target: UploadTargetParams): Promise<UploadCredentials> {
    if (Date.now() < this.#rateLimitedUntil) {
      throw new UploadCredentialsError('rate_limited');
    }
    const query = new URLSearchParams(Object.entries(target).map(([key, value]) => [`target[${key}]`, value]));
    let response: Response;
    try {
      // One attempt: a 429 must not back off into a retry loop.
      response = await fetch(
        `/api/v2/uploads/credentials?${query}`,
        {},
        { intl: null, numRetries: 0, retryStrategy: STRATEGY.DEFAULT, raw: true }
      );
    } catch (error) {
      if (error instanceof RateLimitError) {
        const holdMs = Math.min((error.retryAfterSeconds ?? Infinity) * 1000, RATE_LIMIT_HOLD_MS);
        this.#rateLimitedUntil = Date.now() + holdMs;
      }
      const credentialsError = UploadCredentialsError.fromFetchError(error);
      if (credentialsError.reason === 'unauthorized') {
        // The session lapsed server-side: run the app's login check so the tab recovers like every other 401.
        void this.offline.testLogin();
      }
      throw credentialsError;
    }
    const data: unknown = await response.json().catch(() => undefined);
    if (!isCredentialsResponse(data)) {
      throw new UploadCredentialsError('unavailable');
    }

    return {
      bucket: data.bucket,
      keyPrefix: data.key_prefix,
      region: data.region,
      useAccelerateEndpoint: data.use_accelerate_endpoint,
      identity: {
        accessKeyId: data.access_key_id,
        secretAccessKey: data.secret_access_key,
        sessionToken: data.session_token,
        expiration: toClientTime(data.expiration, response.headers.get('date'))
      }
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'upload-credentials': UploadCredentialsService;
  }
}
